import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';
import { fetchDuesTemplates, addDuesTemplate, updateDuesTemplate, deleteDuesTemplate } from '../api/duesDefinitionApi';

const DuesTemplateView = ({ showToast }) => {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [unitPricePerShare, setUnitPricePerShare] = useState('');
  const [unitPricePerConsumption, setUnitPricePerConsumption] = useState('');
  const [fixedAmount, setFixedAmount] = useState('');
  const [type, setType] = useState('arsaPayi');
  const [responsibleParty, setResponsibleParty] = useState('residentOrDefaultOwner');
  const [recurrenceType, setRecurrenceType] = useState('monthly');
  const [totalAmount, setTotalAmount] = useState('');
  const [distributionMethod, setDistributionMethod] = useState('arsaPayi');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, templateId: null });
  const [isLoading, setIsLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchDuesTemplates();
      setTemplates(data || []);
    } catch (error) {
      showToast("Aidat şablonları yüklenirken hata: " + error.message, "error");
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleAddOrUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !responsibleParty) {
      showToast('Şablon Adı ve Sorumlu Taraf alanları zorunludur.', 'error');
      return;
    }
    
    const templateData = {
      name: name.trim(),
      type,
      unitPricePerShare: type === 'arsaPayi' ? parseFloat(unitPricePerShare) || null : null,
      unitPricePerConsumption: type === 'sayacBazli' ? parseFloat(unitPricePerConsumption) || null : null,
      fixedAmount: type === 'sabitTutar' ? parseFloat(fixedAmount) || null : null,
      responsibleParty,
      recurrenceType,
      totalAmount: totalAmount ? parseFloat(totalAmount) : null,
      distributionMethod,
    };

    setIsLoading(true);
    try {
      if (editingTemplate) {
        await updateDuesTemplate(editingTemplate.id, templateData);
        showToast('Aidat şablonu başarıyla güncellendi.', 'success');
      } else {
        await addDuesTemplate(templateData);
        showToast('Aidat şablonu başarıyla eklendi.', 'success');
      }
      loadTemplates();
      closeModal();
    } catch (error) {
      showToast('Şablon kaydedilirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForEdit = (template) => {
    setEditingTemplate(template);
    setName(template.Name);
    setType(template.Type);
    setUnitPricePerShare(template.UnitPricePerShare?.toString() || '');
    setUnitPricePerConsumption(template.UnitPricePerConsumption?.toString() || '');
    setFixedAmount(template.FixedAmount?.toString() || '');
    setResponsibleParty(template.ResponsibleParty || 'residentOrDefaultOwner');
    setRecurrenceType(template.RecurrenceType || 'monthly');
    setTotalAmount(template.TotalAmount?.toString() || '');
    setDistributionMethod(template.DistributionMethod || 'arsaPayi');
    setIsModalOpen(true);
  };

  const openModalForNew = () => {
    setEditingTemplate(null);
    setName('');
    setType('arsaPayi');
    setUnitPricePerShare('');
    setUnitPricePerConsumption('');
    setFixedAmount('');
    setResponsibleParty('residentOrDefaultOwner');
    setRecurrenceType('monthly');
    setTotalAmount('');
    setDistributionMethod('arsaPayi');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    setName('');
    setUnitPricePerShare('');
    setUnitPricePerConsumption('');
    setFixedAmount('');
    setTotalAmount('');
  };

  const handleDeleteTemplate = async (templateId) => {
    setIsLoading(true);
    try {
      await deleteDuesTemplate(templateId);
      showToast('Aidat şablonu başarıyla silindi.', 'success');
      loadTemplates();
    } catch (error) {
      showToast('Şablon silinirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
      setConfirmDeleteModal({ isOpen: false, templateId: null });
    }
  };

  const getResponsiblePartyDisplayName = (rp) => ({
    'ownerOnly': 'Sadece Kat Maliki',
    'residentOrDefaultOwner': 'Sakin (Yoksa Kat Maliki)'
  }[rp] || rp);

  const getDefinitionTypeDisplayName = (t) => ({
    'arsaPayi': 'Arsa Paylı',
    'sabitTutar': 'Sabit Tutar',
    'sayacBazli': 'Sayaç Bazlı'
  }[t] || t);

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      <ConfirmationModal
        isOpen={confirmDeleteModal.isOpen}
        onClose={() => setConfirmDeleteModal({ isOpen: false, templateId: null })}
        title="Aidat Şablonunu Sil"
        message="Bu aidat şablonunu silmek istediğinizden emin misiniz? Bu işlem, geçmiş dönemlerde bu şablondan oluşturulmuş aidatları etkilemez."
        onConfirm={() => handleDeleteTemplate(confirmDeleteModal.templateId)}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Aidat Şablonları</h1>
        <button onClick={openModalForNew} className="btn-primary-green-style flex items-center">
          <PlusCircle size={18} className="mr-2"/>Yeni Şablon
        </button>
      </div>
      
      <div className="mb-4 p-4 border rounded-lg bg-blue-50 text-blue-800">
        <p><strong>Bilgi:</strong> Burası, her ay otomatik olarak oluşturulacak aidat kalemlerinin (örneğin, apartman görevlisi maaşı, asansör bakımı) ana listesidir. Burada yaptığınız değişiklikler, bir sonraki "Aidat Oluşturma" işleminden itibaren geçerli olacaktır.</p>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingTemplate ? "Aidat Şablonunu Düzenle" : "Yeni Aidat Şablonu"} size="lg">
        <form onSubmit={handleAddOrUpdateTemplate}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="tplName" className="label-form-style">Şablon Adı (örn: Asansör Bakımı):</label>
              <input type="text" id="tplName" value={name} onChange={(e) => setName(e.target.value)} className="input-form-style" required />
            </div>
            <div>
              <label htmlFor="tplRecurrence" className="label-form-style">Tekrar Tipi:</label>
              <select id="tplRecurrence" value={recurrenceType} onChange={(e) => setRecurrenceType(e.target.value)} className="input-form-style">
                <option value="monthly">Aylık</option>
                {/* <option value="yearly">Yıllık</option> */}
              </select>
            </div>
            <div>
              <label htmlFor="tplType" className="label-form-style">Aidat Tipi:</label>
              <select id="tplType" value={type} onChange={(e) => setType(e.target.value)} className="input-form-style">
                <option value="arsaPayi">Arsa Payına Göre</option>
                <option value="sabitTutar">Sabit Tutar (Daire Başına)</option>
                <option value="sayacBazli">Sayaç Bazlı</option>
              </select>
            </div>
            <div>
              <label htmlFor="tplResponsible" className="label-form-style">Sorumlu Taraf:</label>
              <select id="tplResponsible" value={responsibleParty} onChange={(e) => setResponsibleParty(e.target.value)} className="input-form-style">
                <option value="residentOrDefaultOwner">Sakin (Yoksa Kat Maliki)</option>
                <option value="ownerOnly">Sadece Kat Maliki</option>
              </select>
            </div>
            
            {type === 'arsaPayi' && (
              <>
                <div className="md:col-span-2">
                  <label htmlFor="tplUnitPrice" className="label-form-style">Arsa Payı Başına Birim Fiyat (TL, opsiyonel):</label>
                  <input type="number" step="0.01" id="tplUnitPrice" value={unitPricePerShare} onChange={(e) => setUnitPricePerShare(e.target.value)} className="input-form-style" />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="tplTotalAmount" className="label-form-style">Toplam Tutar (TL, opsiyonel):</label>
                  <input type="number" step="0.01" id="tplTotalAmount" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} className="input-form-style" />
                </div>
              </>
            )}
            {type === 'sabitTutar' && (
              <div className="md:col-span-2">
                <label htmlFor="tplFixedAmt" className="label-form-style">Sabit Tutar (TL):</label>
                <input type="number" step="0.01" id="tplFixedAmt" value={fixedAmount} onChange={(e) => setFixedAmount(e.target.value)} className="input-form-style" />
              </div>
            )}
            {type === 'sayacBazli' && (
              <div className="md:col-span-2">
                <label htmlFor="tplUnitPriceConsume" className="label-form-style">Birim Fiyat (TL/m³ veya TL/kWh):</label>
                <input type="number" step="0.01" id="tplUnitPriceConsume" value={unitPricePerConsumption} onChange={(e) => setUnitPricePerConsumption(e.target.value)} className="input-form-style" required />
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button type="button" onClick={closeModal} className="btn-secondary-style">İptal</button>
            <button type="submit" className="btn-primary-green-style" disabled={isLoading}>
              {isLoading ? 'Kaydediliyor...' : (editingTemplate ? "Güncelle" : "Kaydet")}
            </button>
          </div>
        </form>
      </Modal>

      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="th-style">Şablon Adı</th>
              <th className="th-style">Tip</th>
              <th className="th-style">Varsayılan Değer (TL)</th>
              <th className="th-style">Sorumlu</th>
              <th className="th-style">Tekrar</th>
              <th className="th-style">İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map(template => (
              <tr key={template.id}>
                <td className="td-style font-medium">{template.Name}</td>
                <td className="td-style">{getDefinitionTypeDisplayName(template.Type)}</td>
                <td className="td-style">
                  {template.Type === 'arsaPayi' ? (template.TotalAmount ? `${template.TotalAmount.toFixed(2)} (Toplam)` : template.UnitPricePerShare ? `${template.UnitPricePerShare.toFixed(2)} /Pay` : '-') :
                   template.Type === 'sabitTutar' ? (template.FixedAmount ? template.FixedAmount.toFixed(2) : '-') :
                   template.UnitPricePerConsumption ? `${template.UnitPricePerConsumption.toFixed(2)} /Birim` : '-'}
                </td>
                <td className="td-style">{getResponsiblePartyDisplayName(template.ResponsibleParty)}</td>
                <td className="td-style">{template.RecurrenceType === 'monthly' ? 'Aylık' : 'Tek Seferlik'}</td>
                <td className="td-style space-x-2">
                  <button onClick={() => openModalForEdit(template)} className="icon-button-indigo" title="Düzenle"><Edit3 size={18}/></button>
                  <button onClick={() => setConfirmDeleteModal({isOpen: true, templateId: template.id})} className="icon-button-red" title="Sil"><Trash2 size={18}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading && templates.length === 0 && <p>Yükleniyor...</p>}
        {!isLoading && templates.length === 0 && <p className="text-center text-gray-500 py-4">Henüz aidat şablonu eklenmemiş.</p>}
      </div>
    </div>
  );
};

export default DuesTemplateView;
